import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { leaderboardService } from '../../services/leaderboard';
import type { LeaderboardEntry } from '../../types/leaderboard';
import { RankTrophy } from './RankTrophy';

interface LeaderboardPreviewProps {
  gymSlug: string;
}

/** Minimal, card-less leaderboard teaser for the gym profile: top-three avatars + a link. */
export default function LeaderboardPreview({ gymSlug }: LeaderboardPreviewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    leaderboardService
      .getGymLeaderboard(gymSlug)
      .then(data => { if (active) setEntries(data.slice(0, 3)); })
      .catch(() => { if (active) setEntries([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [gymSlug]);

  // Stay invisible while loading or when nobody has logged a send yet.
  if (loading || entries.length === 0) return null;

  const goToLeaderboard = () => navigate(`/gyms/${gymSlug}/leaderboard`);

  return (
    <button
      type="button"
      onClick={goToLeaderboard}
      aria-label={t('leaderboard.goToLeaderboard')}
      className="-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
      data-testid="leaderboard-preview"
    >
      <div className="flex items-center gap-2">
        {entries.map(entry => (
          <div key={entry.user.id} className="relative" data-testid="podium-avatar">
            <Avatar className="h-8 w-8">
              <AvatarImage src={entry.user.avatar_small_url ?? undefined} alt={entry.user.username} />
              <AvatarFallback className="text-xs">{entry.user.username.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-background ring-1 ring-background">
              <RankTrophy rank={entry.rank} className="h-3 w-3" />
            </span>
          </div>
        ))}
      </div>
      <span className="flex items-center gap-1 text-sm font-medium text-primary">
        {t('leaderboard.goToLeaderboard')}
        <ChevronRight className="h-4 w-4" />
      </span>
    </button>
  );
}
